// Drivers — Phase 4 Plan 04-03 (DIS-05); migrated to telegram-ui in
// Phase 12 Plan 12-08 (UIR-07, UI-SPEC §7 Wave 2 + §8 destructive).
//
// List of company drivers as native telegram-ui List/Section/Cell rows with
// Avatar (acronym) + status Badge (DRIVER_STATUS_COLORS bridge) + filter chips
// + search input + sticky "+ Invite driver" CTA (links to
// /dispatcher/invite-driver). Empty state → Placeholder (QR-invite copy).
// Per-driver destructive "Deactivate" → confirm dialog (UI-SPEC §8): red
// Button + "license photo deleted in 90 days" text + haptic warning.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Avatar,
  Badge,
  Button,
  Cell,
  Input,
  List,
  Placeholder,
  Section,
  TabsList,
} from "@telegram-apps/telegram-ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  type DriverListItem,
  type DriverStatus,
  type DriversListFilters,
  driversApi,
} from "../api/drivers";
import { useAuth } from "../auth/useAuth";
import { StatusPill } from "../components/StatusPill";
import { DRIVER_STATUS_COLORS, SPACING } from "../lib/tokens";

type Chip = "all" | "online" | "offline" | "on_trip";

const CHIPS: Array<{ key: Chip; labelKey: string }> = [
  { key: "all", labelKey: "dispatcher:drivers.filter_all" },
  { key: "online", labelKey: "dispatcher:drivers.filter_online" },
  { key: "offline", labelKey: "dispatcher:drivers.filter_offline" },
  { key: "on_trip", labelKey: "dispatcher:drivers.filter_on_trip" },
];

// Badge style-bridge: map driver status → DRIVER_STATUS_COLORS token key.
// telegram-ui <Badge type="dot"> renders the indicator; the colored dot reads
// its fill from the token (light value — dark handled by telegram-ui theme via
// the duplicated StatusPill text, which stays the colorblind-safe a11y signal).
const STATUS_TOKEN_KEY: Record<DriverStatus, keyof typeof DRIVER_STATUS_COLORS> = {
  online: "online",
  offline: "offline",
  on_trip: "activeTrip",
};

function acronymFor(name: string | null, id: number): string {
  if (!name || name.trim() === "") return `#${id}`.slice(0, 2);
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + second).toUpperCase() || `#${id}`.slice(0, 2);
}

function hapticWarning(): void {
  const tg = (
    globalThis as {
      Telegram?: {
        WebApp?: { HapticFeedback?: { notificationOccurred?: (type: string) => void } };
      };
    }
  ).Telegram?.WebApp;
  tg?.HapticFeedback?.notificationOccurred?.("warning");
}

export function Drivers(): React.JSX.Element {
  const { t } = useTranslation();
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [chip, setChip] = useState<Chip>("all");
  const [search, setSearch] = useState<string>("");
  const [confirmDriver, setConfirmDriver] = useState<DriverListItem | null>(null);

  const filters: DriversListFilters = {
    status: chip,
    search: search.trim() || undefined,
    limit: 50,
  };

  const listQuery = useQuery({
    queryKey: ["drivers", filters],
    queryFn: () => driversApi.list(apiClient, filters),
  });

  const deactivateMutation = useMutation({
    mutationFn: (driverId: number) => driversApi.deactivate(apiClient, driverId),
    onSuccess: () => {
      setConfirmDriver(null);
      queryClient.invalidateQueries({ queryKey: ["drivers"] });
    },
  });

  const onRequestDeactivate = (driver: DriverListItem): void => {
    hapticWarning();
    setConfirmDriver(driver);
  };

  const items = listQuery.data?.items ?? [];

  return (
    <List data-testid="drivers" style={{ paddingBottom: 80 }}>
      <Input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={
          t("dispatcher:drivers.search_placeholder", "Search by name or phone…") as string
        }
        data-testid="drivers-search"
      />

      {/* Filter as native telegram-ui TabsList (UI-SPEC §6) — replaces manual tablist div-soup. */}
      <TabsList data-testid="drivers-filter-chips">
        {CHIPS.map((c) => (
          <TabsList.Item
            key={c.key}
            selected={chip === c.key}
            onClick={() => setChip(c.key)}
            data-testid={`drivers-chip-${c.key}`}
          >
            {t(c.labelKey, c.key.replace("_", " "))}
          </TabsList.Item>
        ))}
      </TabsList>

      {listQuery.isLoading ? (
        <p data-testid="drivers-loading">{t("dispatcher:drivers.loading", "Loading…")}</p>
      ) : listQuery.isError ? (
        <p
          data-testid="drivers-error"
          style={{ color: "var(--tgui--destructive_text_color, red)" }}
        >
          {t("dispatcher:drivers.error", "Failed to load drivers.")}
        </p>
      ) : items.length === 0 ? (
        <Placeholder
          data-testid="drivers-empty"
          header={t("dispatcher:drivers.empty.heading", "No drivers yet") as string}
          description={
            t(
              "dispatcher:drivers.empty.body",
              "Invite a driver by QR code to get started.",
            ) as string
          }
        />
      ) : (
        <Section data-testid="drivers-list">
          {items.map((d: DriverListItem) => (
            <Cell
              key={d.id}
              data-testid={`driver-cell-${d.id}`}
              before={
                <Avatar
                  size={40}
                  acronym={acronymFor(d.full_name, d.id)}
                  data-testid={`driver-avatar-${d.id}`}
                />
              }
              subtitle={d.phone ?? undefined}
              after={
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Badge
                    type="dot"
                    style={{ background: DRIVER_STATUS_COLORS[STATUS_TOKEN_KEY[d.status]].bg }}
                  />
                  <StatusPill status={d.status} />
                  <Button
                    size="s"
                    mode="plain"
                    data-testid={`driver-deactivate-${d.id}`}
                    onClick={() => onRequestDeactivate(d)}
                    style={{ color: "var(--tgui--destructive_text_color, #DC2626)" }}
                    aria-label={
                      t(
                        "dispatcher:drivers.confirm.deactivate.title",
                        "Deactivate driver?",
                      ) as string
                    }
                  >
                    ✕
                  </Button>
                </span>
              }
            >
              {d.full_name ?? `Driver #${d.id}`}
            </Cell>
          ))}
        </Section>
      )}

      {/* Sticky bottom CTA — links to Phase 2 InviteDriverScreen */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          padding: SPACING.md,
          background: "var(--tgui--bg_color, white)",
          borderTop: "1px solid var(--tgui--hint_color, #E2E8F0)",
        }}
      >
        <Button
          size="l"
          stretched
          data-testid="drivers-invite-cta"
          onClick={() => navigate("/dispatcher/invite-driver")}
        >
          + {t("dispatcher:drivers.invite_driver", "Invite driver")}
        </Button>
      </div>

      {confirmDriver !== null && (
        <div
          data-testid="driver-deactivate-confirm"
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "flex-end",
            zIndex: 1000,
          }}
          onClick={() => setConfirmDriver(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              background: "var(--tgui--bg_color, white)",
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            }}
          >
            <Section
              header={
                t("dispatcher:drivers.confirm.deactivate.title", "Deactivate driver?") as string
              }
            >
              <Cell multiline>
                {t(
                  "dispatcher:drivers.confirm.deactivate.body",
                  "Deactivate driver {{name}}? The license photo will be auto-deleted in 90 days. Confirm?",
                  { name: confirmDriver.full_name ?? `#${confirmDriver.id}` },
                )}
              </Cell>
              <div style={{ display: "flex", gap: SPACING.sm, padding: SPACING.md }}>
                <Button
                  size="l"
                  mode="plain"
                  stretched
                  data-testid="driver-deactivate-confirm-cancel"
                  onClick={() => setConfirmDriver(null)}
                >
                  {t("dispatcher:common.cancel", "Cancel")}
                </Button>
                <Button
                  size="l"
                  stretched
                  data-testid="driver-deactivate-confirm-ok"
                  disabled={deactivateMutation.isPending}
                  onClick={() => deactivateMutation.mutate(confirmDriver.id)}
                  style={{
                    background: "var(--tgui--destructive_text_color, #DC2626)",
                    color: "#fff",
                  }}
                >
                  {t("dispatcher:drivers.confirm.deactivate.ok", "Deactivate driver")}
                </Button>
              </div>
            </Section>
          </div>
        </div>
      )}
    </List>
  );
}
